import { useQuery } from '@tanstack/react-query'
import { installAppearance } from '@/appearance/client'
import {
  Activity,
  ChevronRight,
  Boxes,
  Calculator,
  Gift,
  HandCoins,
  Image,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  LogOut,
  Menu,
  Moon,
  ScrollText,
  Send,
  Server,
  Sparkles,
  Sun,
  TicketPercent,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  Webhook,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { GlobalSearch } from '@/components/GlobalSearch'
import { QueryErrorToasts } from '@/components/QueryErrorToasts'
import { BrandLockup } from '@/components/ui/BrandLockup'
import { Button } from '@/components/ui/Button'
import { useConfirmOptional } from '@/components/ui/Confirm'
import { RowMenu } from '@/components/ui/RowMenu'
import { useToastOptional } from '@/components/ui/Toast'
import { PrintStamp } from '@/components/ui/PrintStamp'
import { api } from '@/lib/api'
import { canAccess, canDo, useAuth, type Role } from '@/lib/auth'
import { cn } from '@/lib/cn'
import { documentTitle, useDocumentTitle } from '@/lib/documentTitle'
import { fmtInt } from '@/lib/format'
import { roleLabel } from '@/lib/labels'
import { useTheme, type Theme } from '@/lib/theme'
import { useMore } from '@/lib/useMore'
import { groupStats } from '@/lib/webhookLog'
import { backupsQuery, payoutCountsQuery, railPayoutCount, webhookStatsQuery } from '@/lib/attention'
import type { SecretsOverview } from '@/lib/secrets'
import { systemChecks, systemRailAlert, type PayProviderRow, type ProviderKeyRow } from '@/lib/systemHealth'

interface NavItem {
  to: string
  label: string
  section: string
  icon: LucideIcon
}

interface NavGroup {
  id: string
  label: string
  items: NavItem[]
}

/** The rail in five groups (WP-16). Icons are the Mini App's metaphors where they overlap
 *  (WP-12): sparkle for generations, bolt for tariffs, gift for gifts, users for people. */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'ops',
    label: 'Операции',
    items: [
      { to: '/', label: 'Дашборд', section: 'dashboard', icon: LayoutDashboard },
      { to: '/jobs', label: 'Заявки', section: 'jobs', icon: Sparkles },
      { to: '/users', label: 'Пользователи', section: 'users', icon: Users },
      { to: '/activity', label: 'Активность', section: 'activity', icon: Activity },
      { to: '/support', label: 'Поддержка', section: 'support', icon: LifeBuoy },
    ],
  },
  {
    id: 'money',
    label: 'Деньги',
    items: [
      { to: '/finance', label: 'Финансы', section: 'finance', icon: Wallet },
      { to: '/payouts', label: 'Выплаты', section: 'payouts', icon: HandCoins },
      { to: '/accounting', label: 'Бухгалтерия', section: 'accounting', icon: Calculator },
      { to: '/pricing', label: 'Тарифы', section: 'pricing', icon: Zap },
      { to: '/promo', label: 'Промокоды', section: 'promo', icon: TicketPercent },
    ],
  },
  {
    id: 'growth',
    label: 'Рост',
    items: [
      { to: '/acquisition', label: 'Привлечение', section: 'acquisition', icon: TrendingUp },
      { to: '/growth', label: 'Подарки и автопродление', section: 'growth', icon: Gift },
      { to: '/broadcasts', label: 'Рассылки', section: 'broadcasts', icon: Send },
      { to: '/content', label: 'Контент', section: 'content', icon: Image },
    ],
  },
  {
    id: 'catalog',
    label: 'Каталог',
    items: [
      // The model catalog: same section as «Тарифы» (view), changes need pricing.edit.
      { to: '/models', label: 'Модели', section: 'pricing', icon: Boxes },
      { to: '/contacts', label: 'Контакты и ссылки', section: 'contacts', icon: Link2 },
    ],
  },
  {
    id: 'system',
    label: 'Система',
    items: [
      { to: '/webhooks', label: 'Вебхуки', section: 'webhooks', icon: Webhook },
      { to: '/audit', label: 'Аудит', section: 'audit', icon: ScrollText },
      { to: '/system', label: 'Система', section: 'system', icon: Server },
      { to: '/admins', label: 'Операторы', section: 'admins', icon: UserCog },
    ],
  },
]

/** What needs a look, shown as a warn count beside a rail item: the count and its words for a
 *  screen reader («3 новые заявки»), or `alert` with «!» (a failed backup). */
export interface RailBadge {
  count?: number
  /** the number as shown when it is capped: «50+» (the payouts page asks for 51 rows), «99+» */
  shown?: string
  /** «!» instead of a number */
  alert?: boolean
  /** the badge in words (the number alone is not a state, rule 21) */
  label: string
}

/** The section a path opens, by the rail's own words («/payouts» → «Выплаты»): the browser tab
 *  says where it is (WP-25). An address the rail does not know is the in-shell «Такой страницы
 *  нет» (NotFound). */
export function sectionTitle(pathname: string): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  for (const g of NAV_GROUPS) for (const n of g.items) if (n.to === path) return n.label
  return 'Такой страницы нет'
}

const NAV_ID = 'admin-nav-sheet'
const BADGE_REFRESH_MS = 60_000
/** a badge that failed to refresh just keeps its last count: no toast (QueryErrorToasts) */
const SILENT = { silent: true }

function plural(n: number, one: string, few: string, many: string): string {
  const d10 = n % 10
  const d100 = n % 100
  if (d10 === 1 && d100 !== 11) return one
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return few
  return many
}

/**
 * Counts that need an operator (WP-16): new payout requests, open tickets, webhook events that
 * were not applied cleanly in the last 24 hours, a failed backup or another failed System check. Each only for a role that may
 * open the page; a failed request shows no badge (the page itself explains its failure).
 */
function useRailBadges(role: Role | null): Record<string, RailBadge> {
  const payouts = useQuery({
    ...payoutCountsQuery,
    enabled: canAccess(role, 'payouts'),
    refetchInterval: BADGE_REFRESH_MS,
    meta: SILENT,
  })
  const tickets = useQuery({
    queryKey: ['rail', 'tickets'],
    queryFn: () => api.get<{ total: number }>('/support/tickets?status=open&limit=1'),
    enabled: canAccess(role, 'support'),
    refetchInterval: BADGE_REFRESH_MS,
    meta: SILENT,
  })
  const hooks = useQuery({
    ...webhookStatsQuery,
    enabled: canDo(role, 'webhooks.view'),
    refetchInterval: BADGE_REFRESH_MS,
    meta: SILENT,
  })
  const backups = useQuery({
    ...backupsQuery,
    enabled: canDo(role, 'system.keys'),
    refetchInterval: BADGE_REFRESH_MS * 5,
    meta: SILENT,
  })
  // WP-17: the System page's failed checks (its banner) reach the rail too — the owner's only.
  const system = { enabled: canDo(role, 'system.keys'), refetchInterval: BADGE_REFRESH_MS * 5, meta: SILENT }
  const pay = useQuery({
    queryKey: ['rail', 'pay-providers'],
    queryFn: () => api.get<{ items: PayProviderRow[] }>('/system/payment-providers'),
    ...system,
  })
  const keys = useQuery({
    queryKey: ['rail', 'provider-keys'],
    queryFn: () => api.get<{ items: ProviderKeyRow[] }>('/system/keys'),
    ...system,
  })
  const secrets = useQuery({
    queryKey: ['rail', 'secrets'],
    queryFn: () => api.get<SecretsOverview>('/system/secrets'),
    ...system,
  })

  const out: Record<string, RailBadge> = {}
  // GET /payouts/counts: the requests waiting for an operator — new and «На проверке» (the
  // same numbers as the page's tabs; the list's 50-row page no longer caps the badge).
  const newPayouts = railPayoutCount(payouts.data?.counts)
  if (newPayouts > 0) {
    out['/payouts'] = {
      count: newPayouts,
      label: `${fmtInt(newPayouts)} ${plural(newPayouts, 'заявка ждёт', 'заявки ждут', 'заявок ждут')} решения по выплате`,
    }
  }
  const open = tickets.data?.total ?? 0
  if (open > 0) {
    out['/support'] = { count: open, label: `${fmtInt(open)} ${plural(open, 'открытое обращение', 'открытых обращения', 'открытых обращений')}` }
  }
  const problems = [...groupStats(hooks.data?.payments), ...groupStats(hooks.data?.generation)].reduce(
    (n, g) => n + g.problems,
    0,
  )
  if (problems > 0) {
    out['/webhooks'] = {
      count: problems,
      label: `${fmtInt(problems)} ${plural(problems, 'событие требует', 'события требуют', 'событий требуют')} внимания за сутки`,
    }
  }
  const systemAlert = systemRailAlert(
    systemChecks({
      backup: backups.data,
      payProviders: pay.data?.items,
      keys: keys.data?.items,
      secrets: secrets.data,
    }),
    backups.data,
  )
  if (systemAlert !== null) out['/system'] = { alert: true, label: systemAlert }
  return out
}

/**
 * App shell. From 1024 px (lg) the 264 px rail sits beside the content and sticks to the
 * viewport (its list scrolls on its own, the footer stays in reach); below that the rail
 * collapses into a top bar with a «Меню» button that opens the same navigation as a sheet
 * over the page, so a 320 px screen keeps its full width for content (design §10).
 * A page that crashes is caught inside `<main>`: the rail survives (WP-08).
 */
export function Layout({ initialMenuOpen = false }: { initialMenuOpen?: boolean }) {
  useEffect(() => installAppearance('admin'), [])
  const [menuOpen, setMenuOpen] = useState(initialMenuOpen)
  const { pathname } = useLocation()
  const { role, email, logout, logoutEverywhere } = useAuth()
  const [theme, setTheme] = useTheme()
  const badges = useRailBadges(role)
  const nav = { role, email, logout, logoutEverywhere, theme, setTheme, badges }
  useDocumentTitle(documentTitle(sectionTitle(pathname)))

  // Navigating (including browser back) closes the sheet. Only on a real change of path:
  // an unconditional effect also ran on mount and closed a sheet opened via initialMenuOpen.
  const shownPath = useRef(pathname)
  useEffect(() => {
    if (shownPath.current === pathname) return
    shownPath.current = pathname
    setMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  const pending = Object.values(badges).length

  return (
    // `print-shell`: on paper the grid drops its rail column and <main> its padding (index.css).
    <div className="print-shell min-h-dvh bg-void lg:grid lg:grid-cols-[theme(spacing.rail)_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-hairline bg-surface-1 lg:flex print:hidden">
        <NavContent {...nav} />
      </aside>

      <header className="sticky top-0 z-40 flex h-14 items-center justify-between gap-3 border-b border-hairline bg-surface-1 px-4 lg:hidden print:hidden">
        <BrandLockup />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setMenuOpen(true)}
          aria-expanded={menuOpen}
          aria-controls={NAV_ID}
        >
          <Menu aria-hidden size={16} strokeWidth={1.75} />
          Меню
          {pending > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-warn-line bg-warn-soft px-1 text-micro text-warn">
              <span aria-hidden>!</span>
              <span className="sr-only">есть что проверить</span>
            </span>
          )}
        </Button>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden print:hidden">
          <button
            type="button"
            aria-label="Закрыть меню"
            className="absolute inset-0 bg-scrim"
            onClick={() => setMenuOpen(false)}
          />
          <div
            id={NAV_ID}
            role="dialog"
            aria-modal="true"
            aria-label="Навигация"
            className="relative flex h-full w-rail max-w-full flex-col border-r border-hairline bg-surface-1 shadow-e3"
          >
            <NavContent {...nav} onClose={() => setMenuOpen(false)} />
          </div>
        </div>
      )}

      <main className="admin-workspace min-w-0 overflow-x-auto">
        <PrintStamp />
        <div className="admin-topbar flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-surface-1 px-4 py-3 sm:px-6 lg:px-8 print:hidden">
          <div className="hidden min-w-0 items-center gap-2 text-caption text-ink-3 lg:flex" aria-label="Текущий раздел">
            <span>Управление</span>
            <ChevronRight size={14} aria-hidden />
            <span className="text-ink-1">{sectionTitle(pathname)}</span>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-4">
            <GlobalSearch className="lg:max-w-sm" />
            <div className="hidden shrink-0 items-center gap-3 border-l border-hairline pl-4 sm:flex">
              <span className="flex size-8 items-center justify-center rounded-full border border-hairline bg-surface-3 text-caption font-semibold text-ink-2" aria-hidden>
                {(email ?? roleLabel(role ?? '').label).slice(0, 1).toLocaleUpperCase('ru')}
              </span>
              <div className="hidden min-w-0 xl:block">
                <p className="max-w-48 truncate text-caption text-ink-1">{email || 'Учётная запись'}</p>
                <p className="text-caption text-ink-3">{role ? roleLabel(role).label : '—'}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="admin-page mx-auto w-full max-w-shell p-4 sm:p-6 lg:p-8">
          <ErrorBoundary scope="page" resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
      <QueryErrorToasts />
    </div>
  )
}

interface NavContentProps {
  role: Role | null
  email: string | null
  logout: () => void
  /** «Выйти на всех устройствах» (the «⋯» next to «Выйти»); absent in stories without auth */
  logoutEverywhere?: () => Promise<void>
  theme: Theme
  setTheme: (t: Theme) => void
  badges: Record<string, RailBadge>
  onClose?: () => void
}

/** Rail contents, shared by the desktop aside and the mobile sheet (one theme state): the
 *  lockup, the grouped list (scrolls on its own; a fade at its bottom edge says there is more),
 *  and a one-row footer — who is signed in, the theme switch and «Выйти».
 *
 *  Sized so the whole list and every badge fit a 13" laptop window (1440×800) with a mouse:
 *  28 px rows, 8 px between groups, a 48 px header and a 56 px footer, and room for the
 *  longest label («Подарки и автопродление») on one line (§6.15). On a touch screen the rows
 *  grow to 44 px and the list scrolls. */
export function NavContent({
  role,
  email,
  logout,
  logoutEverywhere,
  theme,
  setTheme,
  badges,
  onClose,
}: NavContentProps) {
  const confirm = useConfirmOptional()
  const toast = useToastOptional()
  async function everywhere() {
    if (!logoutEverywhere) return
    const ok = confirm
      ? await confirm({
          title: 'Выйти на всех устройствах?',
          body: 'Закончатся все сеансы этой учётной записи, и этот тоже. Войти снова можно паролем и кодом 2FA.',
          confirmLabel: 'Выйти везде',
          danger: true,
        })
      : true
    if (!ok) return
    try {
      await logoutEverywhere()
    } catch (e) {
      toast?.error(e instanceof Error ? e.message : 'Не удалось завершить сеансы')
    }
  }
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => canAccess(role, n.section)) })).filter(
    (g) => g.items.length > 0,
  )
  const [listRef, more] = useMore<HTMLElement>('y')
  const next: Theme = theme === 'dark' ? 'light' : 'dark'
  const switchLabel = next === 'light' ? 'Включить светлую тему' : 'Включить тёмную тему'
  const ThemeIcon = next === 'light' ? Sun : Moon

  return (
    <>
      <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-hairline px-5">
        <div>
          <BrandLockup />
          <p className="mt-0.5 text-caption text-ink-3">Управление проектом</p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        )}
      </div>
      <nav
        ref={listRef}
        aria-label="Разделы"
        className={cn('admin-navigation min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-3', more && 'fade-bottom')}
      >
        {groups.map((g) => (
          <div key={g.id} className="mt-3 border-t border-hairline pt-2 first:mt-0 first:border-0 first:pt-0 [@media(pointer:coarse)]:mt-4">
            <p id={`nav-group-${g.id}`} className="mb-1 px-3 text-caption text-ink-3">
              {g.label}
            </p>
            <ul aria-labelledby={`nav-group-${g.id}`} className="flex flex-col [@media(pointer:coarse)]:gap-0.5">
              {g.items.map((n) => (
                <li key={n.to}>
                  <RailLink item={n} badge={badges[n.to]} onClick={onClose} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="flex shrink-0 items-center gap-1 border-t border-hairline py-2 pl-5 pr-2">
        <div className="min-w-0 flex-1">
          {email && (
            <p className="truncate text-body leading-5 text-ink-1" title={email}>
              {email}
            </p>
          )}
          <p className="text-caption text-ink-3">{role ? roleLabel(role).label : '—'}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-8 shrink-0 px-0 [@media(pointer:coarse)]:w-11"
          aria-label={switchLabel}
          title={switchLabel}
          onClick={() => setTheme(next)}
        >
          <ThemeIcon aria-hidden size={16} strokeWidth={1.75} />
        </Button>
        {logoutEverywhere && (
          <RowMenu
            label="Сеансы"
            items={[
              { id: 'everywhere', label: 'Выйти на всех устройствах', danger: true, onSelect: () => void everywhere() },
            ]}
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-8 shrink-0 px-0 [@media(pointer:coarse)]:w-11"
          aria-label="Выйти"
          title="Выйти"
          onClick={logout}
        >
          <LogOut aria-hidden size={16} strokeWidth={1.75} />
        </Button>
      </div>
    </>
  )
}

function RailLink({ item, badge, onClick }: { item: NavItem; badge?: RailBadge; onClick?: () => void }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'group flex min-h-7 items-center gap-2.5 rounded-sm border-l-2 border-transparent px-3 py-1 text-body leading-5 text-ink-2',
          'transition-colors duration-micro hover:bg-surface-3 hover:text-ink-1',
          '[@media(pointer:coarse)]:min-h-11',
          isActive && 'border-cyan bg-surface-3 font-medium text-ink-1',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            aria-hidden
            size={16}
            strokeWidth={1.75}
            className={cn('shrink-0', isActive ? 'text-ink-1' : 'text-ink-3 group-hover:text-ink-2')}
          />
          <span className="min-w-0 flex-1">{item.label}</span>
          {badge && (
            <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-warn-line bg-warn-soft px-1.5 text-micro text-warn">
              <span aria-hidden className="tnum">
                {badge.alert ? '!' : (badge.shown ?? ((badge.count ?? 0) > 99 ? '99+' : fmtInt(badge.count ?? 0)))}
              </span>
              <span className="sr-only">{badge.label}</span>
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}
